import React from 'react';
import type { PaginationMeta } from '@yawi/domain';
import { Pagination } from '../Pagination';
import { DataTableHeader } from './DataTableHeader';
import { DataTableEmpty } from './DataTableEmpty';
import { DataTableLoading } from './DataTableLoading';

export interface ColumnDef<T> {
  id: string;
  /** Se renderiza tal cual, así que admite nodos (p. ej. un checkbox de selección). */
  header: React.ReactNode;
  accessorKey?: keyof T;
  cell?: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  // Data
  data: T[];
  columns: ColumnDef<T>[];
  keyExtractor: (item: T) => string;

  // Pagination
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;

  // Header
  title?: string;

  // Search (debounced - handled by usePaginatedList hook)
  searchPlaceholder?: string;
  // Sin `onSearchChange` no se muestra la caja (listados sin búsqueda textual).
  searchValue?: string;
  onSearchChange?: (value: string) => void;

  // Filters
  filters?: React.ReactNode;

  // Actions
  actions?: React.ReactNode;

  // States
  loading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;

  // Row interactions
  onRowClick?: (item: T) => void;
  rowActions?: (item: T) => React.ReactNode;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  meta,
  onPageChange,
  pageSize,
  onPageSizeChange,
  title,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filters,
  actions,
  loading = false,
  emptyMessage,
  emptyIcon,
  onRowClick,
  rowActions,
}: DataTableProps<T>) {
  const getCellValue = (item: T, column: ColumnDef<T>) => {
    if (column.cell) {
      return column.cell(item);
    }
    if (column.accessorKey) {
      const value = item[column.accessorKey];
      return value !== null && value !== undefined ? String(value) : '-';
    }
    return '-';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-300">
      {/* Header with search and filters */}
      <DataTableHeader
        title={title}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        filters={filters}
        actions={actions}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50/80 text-slate-500 border-b border-slate-100">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap ${column.className || ''}`}
                >
                  {column.header}
                </th>
              ))}
              {rowActions && (
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap text-right">Acciones</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)}>
                  <DataTableLoading rows={5} columns={columns.length} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)}>
                  <DataTableEmpty message={emptyMessage} icon={emptyIcon} />
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={keyExtractor(item)}
                  className={`hover:bg-slate-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`px-4 py-3.5 text-slate-800 ${column.className || ''}`}
                    >
                      {getCellValue(item, column)}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="px-4 py-3.5 text-right">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {rowActions(item)}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        meta={meta}
        onPageChange={onPageChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        loading={loading}
      />
    </div>
  );
}
